import {
  IFarmInputQuote,
  IFarmInputQuoteLineItem,
  IQuoteMessage,
  ISupplierUserProfile,
  ISupportingDocumentInput,
} from 'constants/interfaces';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import DeleteIcon from '@material-ui/icons/Delete';
import LibraryAddIcon from '@material-ui/icons/LibraryAdd';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import React, {useCallback, useEffect, useState} from 'react';
import {
  apiADDQUOTELINEITEM,
  apiDeleteQuote,
  apiDELETEQUOTELINEITEM,
  apiDeleteQuoteSupportingDocument,
  apiDownloadQuote,
  apiGetDepotQuote,
  apiGetInputProducts,
  apiGETQUOTEMESSAGES,
  apiGetQuoteSupportingDocuments,
  apiSaveQuoteSupportingDocument,
  apiSENDQUOTETEXTMESSAGE,
  apiSUBMITQUOTE,
  apiUPDATEQUOTELINEITEM,
} from 'constants/Api';
import {FarmerCRMCard} from 'screens/Dashboard/Invoices/FarmerDetailCard/FarmerCRMCard';
import {Route} from 'react-router-dom';
import {QuoteDetailCard} from 'screens/Dashboard/Quotes/QuoteDetailCard/QuoteDetailCard';
import {QuoteChat} from 'screens/Dashboard/Quotes/QuoteChat/QuoteChat';
import {QuoteProductInfo} from 'screens/Dashboard/Quotes/QuoteProducts/QuoteProducts';
import {NavigationService} from 'services/index';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import {
  reduxRemoveQuote,
  reduxSetProducts,
  reduxUpdateQuote,
} from 'appredux/actions';
import {ProductState} from 'appredux/reducers/reducer-products';
import moment from 'moment';
import {useSocketEvents} from 'appsockets/SocketEventProvider';
import {PermissionsService} from 'services/PermissionsService';
import {
  EInputOrderPaymentType,
  IlogisticsTypeId,
  ESupplierUserPermissions,
} from 'constants/index';
import {SupportingDocuments} from 'components/SupportingDocuments/SupportingDocuments';
import {EPurchaseOrderSupportingDocs} from 'constants/index';
import {Breakpoint} from '@material-ui/core/styles/createBreakpoints';
import {CreateOrderFromQuoteDialog} from 'screens/Dashboard/Quotes/QuoteDetail/CreateOrderFromQuoteDialog';

import 'screens/Dashboard/Invoices/Invoices.scss';
import './QuoteDetail.scss';
import {apiCheckoutQuote} from 'constants/api/orders';

export const QuoteDetail = (props: {
  user: ISupplierUserProfile;
  quoteId: number;
  productState: ProductState;
  width: Breakpoint;
}) => {
  const {user, quoteId} = props;

  const [tabIndex, setTabIndex] = useState(0);
  const showTabs = props.width === 'xs' || props.width === 'sm';

  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quote, setQuote] = useState<IFarmInputQuote>();
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isRefreshingMessages, setIsRefreshingMessages] = useState(false);
  const [messages, setMessages] = useState<IQuoteMessage[]>([]);
  const [isLoadingAllProducts, setIsLoadingAllProducts] = useState(false);

  const [lineItemsToUpdate, setLineItemsToUpdate] = useState<
    IFarmInputQuoteLineItem[]
  >([]);
  const [lineItemsToAdd, setLineItemsToAdd] = useState<
    IFarmInputQuoteLineItem[]
  >([]);
  const [lineItemsToRemove, setLineItemsToRemove] = useState<number[]>([]);
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  const [showSubmittedQuoteSnackbar, setShowSubmittedQuoteSnackbar] =
    useState(false);
  const [isDownloadingQuote, setIsDownloadingQuote] = useState(false);
  const [showCreateOrderConfirmation, setShowCreateOrderConfirmation] =
    useState(false);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const {newQuoteMessage} = useSocketEvents();

  const loadQuote = useCallback(
    (id: number) => {
      setIsLoadingQuote(true);
      apiGetDepotQuote(id)
        .then((fetchedQuote) => {
          setQuote(fetchedQuote);
          reduxUpdateQuote(fetchedQuote);
        })
        .catch((error) => {
          alert('Error loading quote. Please try again later');
          console.error(error);
        })
        .finally(() => setIsLoadingQuote(false));
    },
    [quoteId],
  );

  useEffect(() => {
    loadQuote(quoteId);
  }, [quoteId, loadQuote]);

  const loadMessages = useCallback((id: number) => {
    setIsLoadingMessages(true);
    apiGETQUOTEMESSAGES(id)
      .then(setMessages)
      .catch((error) => {
        alert('Error loading messages. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingMessages(false));
  }, []);

  const refreshMessages = (id: number) => {
    setIsRefreshingMessages(true);
    apiGETQUOTEMESSAGES(id)
      .then(setMessages)
      .catch((error) => {
        alert('Error loading messages. Please try again later');
        console.error(error);
      })
      .finally(() => setIsRefreshingMessages(false));
  };

  useEffect(() => {
    loadMessages(quoteId);
  }, [quoteId, loadMessages]);

  useEffect(() => {
    if (newQuoteMessage?.quote.id === quoteId) {
      refreshMessages(quoteId);
    }
  }, [newQuoteMessage, quoteId]);

  const loadAllProducts = useCallback(() => {
    const {user, productState} = props;
    const {lastUpdate, products} = productState;

    const productsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(8, 'hours'),
    );
    const noProducts = !products?.length;

    if (productsAreExpired || noProducts) {
      setIsLoadingAllProducts(true);
      apiGetInputProducts(user)
        .then((fetchedProducts) => reduxSetProducts(fetchedProducts))
        .catch((error) => {
          alert('Error loading products. Please try again later');
          console.error(error);
        })
        .finally(() => setIsLoadingAllProducts(false));
    }
  }, [props]);

  useEffect(loadAllProducts, [loadAllProducts]);

  const handleQuoteSubmit = (id: number) => {
    const addPromises = lineItemsToAdd.map((li) => handleAddLineItem(id, li));
    const updatePromises = lineItemsToUpdate.map((li) =>
      handleUpdateLineItem(li),
    );
    const removePromises = lineItemsToRemove.map((liId) =>
      handleRemoveLineItem(liId),
    );
    const allPromises = [...addPromises, ...updatePromises, ...removePromises];

    setIsSubmittingQuote(true);
    return Promise.all(allPromises)
      .then(() => apiSUBMITQUOTE(id))
      .then(() => setShowSubmittedQuoteSnackbar(true))
      .then(() => loadQuote(id))
      .catch((err) => {
        console.error('Error submitting quote:', err);
        alert('Error submitting quote. Please try again later');
      })
      .finally(() => setTimeout(() => setIsSubmittingQuote(false), 500));
  };

  const handleAddLineItem = (
    id: number,
    lineItemToAdd: IFarmInputQuoteLineItem,
  ) => {
    const lineItem = sanitizeLineItem(lineItemToAdd);
    return apiADDQUOTELINEITEM(id, lineItem).catch((err) => {
      console.error('Failed to add line item', lineItemToAdd, err);
      throw err;
    });
  };

  const handleUpdateLineItem = (lineItem: IFarmInputQuoteLineItem) => {
    return apiUPDATEQUOTELINEITEM(sanitizeLineItem(lineItem)).catch((err) => {
      console.error('Failed to update line item', lineItem, err);
      throw err;
    });
  };

  const sanitizeLineItem = (lineItem: IFarmInputQuoteLineItem) =>
    user.isCommodityGroup
      ? {...lineItem, price: lineItem.product.price, costPrice: undefined}
      : lineItem;

  const handleRemoveLineItem = (lineItemId: number) => {
    return apiDELETEQUOTELINEITEM(lineItemId).catch((err) => {
      console.error('Failed to remove line item', lineItemId, err);
      throw err;
    });
  };

  const handleMessageSend = (id: number, text: string) => {
    return apiSENDQUOTETEXTMESSAGE(id, text)
      .then((response) => {
        if (response) refreshMessages(id);
        else throw new Error('Error sending message');
      })
      .catch((error) => {
        alert('Error sending message');
        console.error(error);
        throw error;
      });
  };

  const handleDownload = () => {
    setIsDownloadingQuote(true);
    apiDownloadQuote(quoteId)
      .then((url) => createCacheAvoidingUrl(url))
      .then((finalUrl) => {
        const newWindow = window.open(finalUrl, '_blank');
        if (newWindow) newWindow.opener = null;
      })
      .catch((err) => {
        alert('Failed to download quote. Please try again later');
        console.error('Failed to download quote', err);
      })
      .finally(() => setIsDownloadingQuote(false));
  };

  function createCacheAvoidingUrl(url: string) {
    const searchParamToAvoidCache = `date=${new Date().getTime()}`;
    const appendChar = url.includes('?') ? '&' : '?';
    return `${url}${appendChar}${searchParamToAvoidCache}`;
  }

  const handleCopyQuote = () => {
    NavigationService.navigate('/create-quote?quote=' + quoteId);
  };

  const handleCreateOrder = (
    isVatExempt: boolean,
    logisticsTypeId: IlogisticsTypeId,
    logisticsCost: number,
    paymentOptions: {
      paymentType: EInputOrderPaymentType;
      gpayAccountIdsByProductId?: Record<string, string | number>;
    },
    contactPersonName?: string,
    contactPersonPhoneNumber?: string,
    deliveryInstructions?: string,
  ) => {
    setShowCreateOrderConfirmation(false);
    setIsCreatingOrder(true);

    return apiCheckoutQuote(
      user,
      quote!,
      logisticsTypeId,
      isVatExempt,
      logisticsCost,
      {
        contactPersonName,
        contactPersonPhoneNumber,
        deliveryInstructions,
      },
      paymentOptions,
    )
      .then((resp) => resp.proformaInvoiceId ?? resp.invoiceId)
      .catch((err) => {
        const errorMessage = Array.isArray(err)
          ? err.join(', ')
          : err?.message || err;
        alert('Failed to create order: ' + errorMessage);
        console.error('Failed to create order from quote', err);
        throw err;
      })
      .finally(() => setIsCreatingOrder(false));
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(false);
    return apiDeleteQuote(quoteId)
      .then(() => reduxRemoveQuote(String(quoteId)))
      .catch((err) => {
        alert('Failed to delete quote. Please try again later');
        console.error('Failed to delete quote', err);
        throw err;
      });
  };

  const loadSupportingDocuments = useCallback(() => {
    return apiGetQuoteSupportingDocuments(quoteId);
  }, [quoteId]);

  const updateSupportingDocument = useCallback(
    (doc: ISupportingDocumentInput) => {
      return apiSaveQuoteSupportingDocument({quoteId, ...doc}).then(
        () => undefined,
      );
    },
    [quoteId],
  );

  const removeSupportingDocument = useCallback((docId: string | number) => {
    return apiDeleteQuoteSupportingDocument(docId).then(() => undefined);
  }, []);

  const activeProducts = props.productState.products.filter((p) => p.isActive);

  // Permissions
  const permissions = new PermissionsService(user);
  const canWriteQuote = permissions.hasPermission(
    ESupplierUserPermissions.QUOTE_WRITE,
  );
  const canWriteOrder = permissions.hasPermission(
    ESupplierUserPermissions.ORDER_WRITE,
  );
  const canReadChat = permissions.hasPermission(
    ESupplierUserPermissions.QUOTE_CHAT_READ,
  );
  const canWriteChat = permissions.hasPermission(
    ESupplierUserPermissions.QUOTE_CHAT_WRITE,
  );
  const canWriteDocs = permissions.hasPermission(
    ESupplierUserPermissions.QUOTE_MANAGE_DOCUMENTS,
  );

  return (
    <div className="quote-detail">
      <AppBar className="app-bar" elevation={0}>
        <Toolbar>
          <Route
            render={({history}) => (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() =>
                  history.location.key
                    ? history.goBack()
                    : history.replace('/quotes')
                }
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          />
          <Typography variant="h6" className="title">
            Quote {quote?.quoteNumber}
          </Typography>

          <Tooltip title="Download" aria-label="download">
            <IconButton
              edge="start"
              color="inherit"
              onClick={!isDownloadingQuote ? handleDownload : () => null}
              aria-label="download"
            >
              {isDownloadingQuote ? (
                <CircularProgress color="inherit" size={20} thickness={6} />
              ) : (
                <GetAppIcon />
              )}
            </IconButton>
          </Tooltip>

          {/* Our custom "MyHidden" wrapper around Hidden, to fix the children type error */}
          <div>
            <Box display="flex" flexDirection="row" alignItems="center">
              {canWriteQuote && (
                <Tooltip
                  title="Copy To New Quote"
                  aria-label="copy to new quote"
                >
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleCopyQuote}
                    aria-label="copy to new quote"
                    style={{marginLeft: 4}}
                  >
                    <LibraryAddIcon />
                  </IconButton>
                </Tooltip>
              )}
              {canWriteOrder && (
                <Tooltip
                  title="Create Order From Quote"
                  aria-label="create order from quote"
                >
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={() => {
                      if (!quote || isCreatingOrder) return;
                      setShowCreateOrderConfirmation(true);
                    }}
                    aria-label="create order from quote"
                    style={{marginLeft: 4}}
                  >
                    {isCreatingOrder ? (
                      <CircularProgress
                        color="inherit"
                        size={20}
                        thickness={6}
                      />
                    ) : (
                      <LocalShippingIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              {canWriteQuote && (
                <Tooltip title="Delete" aria-label="delete">
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={() => setShowDeleteConfirmation(true)}
                    aria-label="delete"
                    style={{marginLeft: 4}}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </div>
        </Toolbar>
      </AppBar>

      <AppBar
        position="relative"
        elevation={0}
        variant="outlined"
        className="tabs-container"
        style={{display: showTabs ? '' : 'none'}}
      >
        <Tabs
          value={tabIndex}
          onChange={(event: React.ChangeEvent<{}>, newIndex: number) =>
            setTabIndex(newIndex)
          }
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value={0} label="Details" />
          <Tab value={1} label="Chat" />
        </Tabs>
      </AppBar>

      <Grid container className="container">
        {/* Left side (Details) */}
        <Grid
          item
          xs={12}
          md={8}
          className="grid-column quote-detail-details-column"
          style={{display: showTabs && tabIndex === 1 ? 'none' : ''}}
        >
          <Paper className="order-info-container" variant="outlined">
            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">Quote Details</Typography>
              </Box>
              <QuoteDetailCard
                isLoading={isLoadingQuote}
                quote={quote}
                user={user}
              />
            </Box>

            <Box className="farm-details-container">
              <FarmerCRMCard
                isLoading={isLoadingQuote}
                farmer={quote?.farmer}
                user={user}
                onSave={() => loadQuote(quoteId)}
              />
            </Box>

            <Box className="order-products-container-2">
              <Box className="header-container">
                <Typography variant="subtitle1">Line Items</Typography>
              </Box>
              <QuoteProductInfo
                isLoading={isLoadingAllProducts}
                quote={quote}
                allInputProducts={activeProducts}
                onLineItemAdd={(item) =>
                  setLineItemsToAdd([...lineItemsToAdd, item])
                }
                onLineItemUpdate={(item) =>
                  setLineItemsToUpdate([...lineItemsToUpdate, item])
                }
                onLineItemRemove={(lineItemId) =>
                  setLineItemsToRemove([...lineItemsToRemove, lineItemId])
                }
                onSubmitQuote={() => handleQuoteSubmit(quoteId)}
                isSubmittingQuote={isSubmittingQuote}
                readonly={!canWriteQuote}
                isCommodityGroup={user.isCommodityGroup}
              />
            </Box>

            <Box className="farm-details-container">
              <Box className="header-container">
                <Typography variant="subtitle1">
                  Supporting Documents
                </Typography>
              </Box>
              <SupportingDocuments
                readonly={!canWriteDocs}
                loadSupportingDocuments={loadSupportingDocuments}
                updateSupportingDocument={updateSupportingDocument}
                removeSupportingDocument={removeSupportingDocument}
                titleOptions={[
                  EPurchaseOrderSupportingDocs.Waybill,
                  EPurchaseOrderSupportingDocs.DeliveryNote,
                  EPurchaseOrderSupportingDocs.Invoice,
                ]}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Right side (Chat) */}
        <Grid
          item
          xs={12}
          md={4}
          className="grid-column quote-detail-chat-column"
          style={{display: showTabs && tabIndex === 0 ? 'none' : ''}}
        >
          {canReadChat && (
            <QuoteChat
              isLoading={isLoadingMessages}
              isRefreshing={isRefreshingMessages}
              myProfilePicture={user.pictureUrl}
              quote={quote}
              messages={messages}
              onMessageSend={(text) => handleMessageSend(quoteId, text)}
              readonly={!canWriteChat}
              header={true}
            />
          )}
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        open={showSubmittedQuoteSnackbar}
        onClose={() => setShowSubmittedQuoteSnackbar(false)}
        autoHideDuration={3000}
        message="Quote submitted"
      />

      <Route>
        <CreateOrderFromQuoteDialog
          isOpen={showCreateOrderConfirmation}
          quote={quote}
          onClose={() => setShowCreateOrderConfirmation(false)}
          onConfirm={(
            isVatExempt,
            logisticsTypeId,
            logisticsCost,
            paymentOptions,
            contactPersonName,
            contactPersonPhoneNumber,
            deliveryInstructions,
          ) =>
            handleCreateOrder(
              isVatExempt,
              logisticsTypeId,
              logisticsCost,
              paymentOptions,
              contactPersonName,
              contactPersonPhoneNumber,
              deliveryInstructions,
            ).then((orderId) =>
              NavigationService.navigate('/orders/' + orderId),
            )
          }
        />
        <DeleteQuoteConfirmationDialog
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={() =>
            handleDelete().then(() => NavigationService.goBack())
          }
        />
      </Route>
    </div>
  );
};

const DeleteQuoteConfirmationDialog = (props: {
  isOpen: boolean;
  onClose: () => any;
  onConfirm: () => any;
}) => {
  return (
    <Dialog
      className="quote-line-item-remove-confirmation-dialog"
      disableEscapeKeyDown
      open={props.isOpen}
      onClose={props.onClose}
    >
      <DialogTitle className="heading">Delete quote</DialogTitle>
      <DialogContent className="body">
        Are you sure you want to delete the quote? You will not be able to undo.
      </DialogContent>
      <DialogActions>
        <Button className="cancel-button" onClick={props.onClose}>
          Cancel
        </Button>
        <Button className="accept-button" onClick={props.onConfirm}>
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
};
